import api from './api';

export interface AgentFactoryOption {
  value: string;
  label: string;
  description?: string;
  group?: string;
  structured?: boolean;
  required?: boolean;
}

export interface AgentFactoryOptions {
  enabled: boolean;
  version: string;
  projectKinds: AgentFactoryOption[];
  domains: AgentFactoryOption[];
  dataSources: AgentFactoryOption[];
  capabilities: AgentFactoryOption[];
  channels: AgentFactoryOption[];
}

export interface AgentFactoryTestRun {
  runId: string;
  status: 'running' | 'passed' | 'failed';
  totalCases: number;
  passedCases: number;
  failedCases: number;
  createdAt?: string;
  completedAt?: string;
  report?: Record<string, any>;
}

export interface AgentFactoryProjectStatus {
  enabled: boolean;
  isBusinessAgent: boolean;
  projectId: string;
  projectName?: string;
  businessGoal?: string;
  config?: {
    domain: string;
    dataSources: string[];
    capabilities: string[];
    experienceChannels: string[];
    designStatus: 'draft' | 'ready' | 'confirmed' | 'stale';
    designHash?: string;
  };
  design?: {
    status: 'draft' | 'ready' | 'confirmed' | 'stale';
    ready: boolean;
    missingFiles: string[];
    validationErrors?: string[];
    hash?: string;
    approval?: Record<string, any>;
    productDesign?: string;
    technicalDesign?: string;
    visualDesign?: string;
    blueprint?: Record<string, any>;
    manifest?: string;
  };
  development?: {
    manifestReady: boolean;
    productionReadinessReady: boolean;
    dockerReady: boolean;
    environmentReady: boolean;
    visualQaRequired?: boolean;
    visualQaReady?: boolean;
    validationErrors?: string[];
    manifest?: Record<string, any>;
    productionReadiness?: string;
    visualQaReport?: string;
    visualEvidenceManifest?: Record<string, any>;
    visualAttestation?: {
      verified: boolean;
      verifiedAt?: string;
      reviewedImageCount?: number;
      errors?: string[];
    };
  };
  tests?: AgentFactoryTestRun[];
  delivery?: {
    status: 'not_started' | 'submitted' | 'approved' | 'listed';
    agentCode?: string;
    reviewId?: string;
    serviceBaseUrl?: string;
    standaloneUrl?: string;
    submittedAt?: string;
  };
}

export interface AgentFactoryAutoTestInput {
  seedQuestions: string[];
  generatedCount: number;
  maxEvolutionRounds: number;
}

export async function getAgentFactoryOptions(): Promise<AgentFactoryOptions> {
  const { data } = await api.get('/agent-factory/options');
  return data;
}

export async function getAgentFactoryProjectStatus(projectId: string): Promise<AgentFactoryProjectStatus> {
  const { data } = await api.get(`/agent-factory/projects/${projectId}`);
  return data;
}

export async function confirmAgentFactoryDesign(projectId: string) {
  const { data } = await api.post(`/agent-factory/projects/${projectId}/confirm-design`, {});
  return data;
}

export async function submitAgentFactoryDelivery(
  projectId: string,
  payload: { serviceBaseUrl?: string; standaloneUrl?: string; serviceToken?: string },
) {
  const { data } = await api.post(`/agent-factory/projects/${projectId}/delivery`, payload);
  return data;
}
